import { Identifier, RaRecord } from "react-admin";

import { CustomFieldRecordType } from "./CustomField/CustomField.types";
import { GeneratedDocumentRecordType } from "./DocumentTemplate/DocumentTemplate.types";
import { EventFeedbackFormRecordType, EventStatusEnum, NonStaffEventRecordType } from "./Event/Event.types";

export type NoteRecordType = {
  id: number;
  text: string;
  related_id: number;
  related_resource?: string;
  meta: {
    changed_by_name: string;
    related_name: string;
  };
  updated_at: string;
  high_priority: boolean;
};

export type UploadFileRecordType = {
  data: string;
  related_resource?: string | null;
  related_id?: number | null;
  original_filename: string;
  tags: string[];
};

export type FileVisibilityFlags = {
  visible_for_all: boolean;
  visible_for_attendees: boolean;
  visible_for_lecturers: boolean;
};

export type FileVisibilityFlag = keyof FileVisibilityFlags;

export type NonStaffFileRecordType = {
  id: number;
  description: string | null;
  name: string | null;
  original_filename: string;
  mime_types: string[];
  url: string;
  created_at: string;
};

export type FileRecordType = FileVisibilityFlags &
  NonStaffFileRecordType & {
    tags: string[];
    related_id?: number | null;
    related_resource?: string | null;
    file_size: number | null;
    created_by: number | null;
  };

export type TaxRateRecordType = {
  id: number;
  amount: number;
  is_default: boolean;
  untdid_category: string;
  untdid_category_display: string;
};

export type PriceTypeRecordType = {
  id: number;
  name: string;
  short_name?: string;
  archived_at: string | null;
};

export type PricePositionRecordType = {
  id?: number;
  amount?: number;
  name: string;
  quantity: number;
  sales_tax: number;
  tax_rate: number | null;
  tax_rate_untdid_category: number | null;
  order_item?: number | null;
  inventory_account?: string | null;
};

export type PriceRecordType = {
  amount: number;
  cached_amount: number;
  created_at: string;
  id: number;
  positions: PricePositionRecordType[];
  price_type: number;
  price_type_name: string;
  price_type_short_name: string;
  valid_until?: string;
  valid_from?: string;
  updated_at: string;
};

export type AddressRecordType = {
  id: number;
  address_line_1: string | null;
  address_line_2: string | null;
  co: string | null;
  city: string | null;
  postcode: string | null;
  state: string | null;
  countrycode: string | null;
  country: string | null;
};

export type ContactInfoType = AddressRecordType & {
  email: string | null;
  fax: string | null;
  mobile: string | null;
  organization_name: string | null;
  website: string | null;
  phone: string | null;
  kind: string;
};

export type ContactInfoFieldsType = keyof ContactInfoType;

export type MoodleUserRecordType = {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  title: undefined;
  has_auth_token: boolean;
  edm_user: number;
};

export type MoodleEnrolledUserRecordType = {
  role: number;
  active: boolean;
} & MoodleUserRecordType;

export type MoodleCourseRecordType = {
  id: number;
  id_number: string;
  category: number;
  name: string;
  short_name: string;
  start_date: string;
  end_date: string;
};

export type PermissionType = {
  id: number;
  action: string;
  related_resource: string;
};

export type MinimalUserRecordType = {
  id: number;
  title: string | null;
  first_name: string;
  last_name: string;
  image?: FileRecordType | null;
};

export type MinimalUserWithContactInfoRecordType = {
  main_contact_info: ContactInfoType | null;
  invoice_contact_info: ContactInfoType | null;
} & MinimalUserRecordType;

export type MinimalUserRecordTypeWithTooltip = {
  tooltip: string;
} & MinimalUserRecordType;

/** The contact-info triple every person-shaped record carries. */
export type PersonContactInfosType = {
  main_contact_info: ContactInfoType | null;
  invoice_contact_info: ContactInfoType | null;
  invoice_dispatch_contact_info: ContactInfoType | null;
};

export type UserRecordType = MinimalUserRecordType & {
  birthday: string | null;
  description: string | null;
  gender: string | null;
  invoice_contact_info: ContactInfoType | null;
  invoice_dispatch_contact_info: ContactInfoType | null;
  main_contact_info: ContactInfoType | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  documents: FileRecordType[];
  moodle_user: MoodleUserRecordType | null;
  group_permissions: PermissionType[];
  is_staff: boolean;
  is_admin: boolean;
  is_attendee: boolean;
  is_lecturer: boolean;
  is_2fa_required: boolean;
  is_2fa_enabled: boolean;
  debit_number?: string | null;
  is_unverified?: boolean;
  login_email: string | null;
  role_permission_groups: string[];
  accepted_consent_scopes?: string[];
  manage_emailing_senders?: boolean;
  organization_memberships?: MinimalOrganizationMembership[];
  language: string;
  tags: string[];
  custom_fields_data?: CustomFieldDataType;
  meta?: {
    organization_memberships?: MinimalOrganizationMembership[];
    attendee_number?: string | null;
    custom_fields_meta?: CustomFieldMetaType;
    [key: string]: unknown;
  };
};

export type ProfileRecordType = MinimalUserRecordType & {
  invoice_contact_info: ContactInfoType | null;
  invoice_dispatch_contact_info: ContactInfoType | null;
  main_contact_info: ContactInfoType | null;
  login_email: string | null;
  birthday: string | null;
  description: string | null;
  gender: string | null;
  custom_fields_data?: CustomFieldDataType;
  custom_fields: CustomFieldRecordType[];
  language: string;
  list_entries: UserListDataRecordType[];
  consents: ConsentRecordType[];
  attendee_number?: string;
};

export type MinimalOrganizationMembership = {
  id: number;
  organization_id: number;
  name: string;
  can_edit_organization: boolean;
  can_enroll: boolean;
  can_manage_users: boolean;
};

export type AttendeeRecordType = UserRecordType & {
  debit_number?: string;
  audience_role?: number;
  attendee_number?: string;
  custom_fields_data?: CustomFieldDataType;
  attendee?: number;
  meta: {
    custom_fields_meta?: CustomFieldMetaType;
    organization_memberships?: MinimalOrganizationMembership[];
  };
};

export type AttendeeInterestRecordType = {
  id: number;
  user: number;
  event: number;
  meta: {
    event: NonStaffEventRecordType;
  };
};

export type InterestRecordType = {
  created_at: string;
  event: number;
  id: number;
  user: number;
  updated_at: string;
  meta: {
    user: UserRecordType;
  };
};

export type InterestWithUserRecordType = InterestRecordType & {
  user: UserRecordType;
};

export type LecturerRecordType = UserRecordType & {
  common_fee: number;
  qualifications: string[];
  custom_fields_data?: CustomFieldDataType;
  events: number[];
};

export type PersonRecordType = {
  id: Identifier;
  first_name: string;
  last_name: string;
  gender?: string | null;
  title?: string | null;
  birthday?: string | null;
  description?: string | null;
  invoice_contact_info?: ContactInfoType | null;
  invoice_dispatch_contact_info?: ContactInfoType | null;
  main_contact_info?: ContactInfoType | null;
  login_email?: string | null;
  archived_at?: string | null;
};

export type PersonWithImageRecordType = {
  image?: FileRecordType;
} & PersonRecordType;

export type CustomFieldDataType = {
  [key: string]: string | boolean | Date | number;
};

export type CustomFieldMetaType = {
  [key: string]: {
    display: string | string[];
    label: string;
    resource: string;
    ids?: (number | string)[];
  };
};

export type RecordWithFileRecordType = RaRecord & {
  [key: string]: FileRecordType | FileRecordType[] | undefined;
};

export type CustomerInvoiceRecordType = {
  id: number;
  commission_sum: number;
  commission_per_month: number;
  discount: number;
  minimum_price_per_month: number;
  from_datetime: Date;
  to_datetime: Date;
  created_at: Date;
};

export type CustomerInvoicePositionRecordType = {
  commission: number;
  booking_amount: number;
  booking_number: string;
  event_name: string;
  attendee_name: string;
  order: number | null;
  order_number: string | null;
  created_at: Date;
};

export type EventAttendanceAPIType = {
  booking: number;
  event: number;
};

export type EventAttendanceRecordType = {
  id: Identifier;
  booking: number;
  event: number;
  event_date?: Identifier;
  comment?: string;
  absences: AttendanceAbsenceRecordType[];
  has_attended: boolean | null;
  meta?: {
    event_id_number: string;
    event_base_name: string;
    event_status: string;
  };
};

export type AttendanceAbsenceRecordType = {
  id: Identifier;
  event_attendance: Identifier;
  absence_start: string;
  absence_end: string;
};

export type TodoRecordType = {
  id: number;
  text: string;
  assignee: number;
  is_done: boolean;
  position: number;
  due_date?: Date;
  meta: {
    assignee_first_name?: string;
    assignee_last_name?: string;
    assignee_color?: string;
  };
  event_meta?: {
    event_name: string;
    event_status: EventStatusEnum;
    event_id_number: string;
  };
  event?: number;
  todo_list: number;
  created_by: number;
};

export type TodoListRecordType = {
  id: number;
  name: string;
  todos: TodoRecordType[];
  created_at: string;
};

export type GenerateDocumentPersistResponseType = GeneratedDocumentRecordType[];

export type GenerateDocumentResponseType = GenerateDocumentTaskRecordType;

export type GenerateDocumentTaskResponseType = GenerateDocumentTaskRecordType;

export type GenerateDocumentTaskRecordType = {
  id: number;
  state: string;
  created_at: string;
  total_count: number;
  current_count?: number;
  other_pending_documents_count?: number;
  state_message: string | null;
  documents?: GeneratedDocumentRecordType[];
};

export type PermissionRecordType = {
  id: number;
  related_resource: string;
  action: string;
};

export type EmailSenderRecordType = {
  id: number;
  name: string;
  email: string;
};

export type QuestionSelectValueRecordType = {
  id: number;
  value: string;
};

export type QuestionRecordType = {
  id: number;
  text: string;
  type: string;
  meta: {
    select_values: QuestionSelectValueRecordType[];
  };
};

export type FeedbackFormQuestionGroupRecordType = {
  id: number;
  name: string;
  description?: string;
  questions: FeedbackFormQuestionGroupQuestionRecordType[];
  position: number;
  feedback_form?: number;
  is_default: boolean;
  is_template: boolean;
};

export type FeedbackFormQuestionGroupQuestionRecordType = {
  id: number;
  question: QuestionRecordType;
  feedback_form: number;
  question_group: number;
  position: number;
  archived_at?: string | null;
};

export type FeedbackFormRecordType = {
  id: number;
  name: string;
  introduction?: string;
  closing_remark?: string;
  questions: FeedbackFormQuestionGroupQuestionRecordType[];
  question_groups: FeedbackFormQuestionGroupRecordType[];
};

export type FeedbackResponseFormRecordType = {
  id: number;
  feedback_form: FeedbackFormRecordType;
  event_name: string;
};

export type ResponseRecordType = {
  id: number;
  rating: number | null;
  question: number;
  free_text: string | null;
  value: string | string[];
  created_at: string;
  meta: {
    question: QuestionRecordType;
  };
};

export type ResponseFormRecordType = {
  id: number;
  event_feedback_form: number;
  responses: ResponseRecordType[];
  created_at: string;
  meta: {
    event_number: string;
    event_base_name: string;
    event_status: EventStatusEnum | undefined;
    event_feedback_form: EventFeedbackFormRecordType;
    questions: QuestionRecordType[];
  };
};

export type QuestionStatRecordType = {
  id: number;
  text: string;
  average_rating: number;
  response_count: number;
  value_percentages: object;
  type: string;
};

export type PublicOptionRecordType = {
  id: number;
  key: string;
  value?: string;
};

export type ConsentTypeRecordType = {
  id: number;
  name: string;
  description: string;
  scope: string;
  needs_confirmation: boolean;
  welcome_mail_template?: number;
  confirmation_mail_template?: number;
};

export type ConsentRecordType = {
  id: number;
  consent_type: number;
  user: number | null;
  order: number | null;
  token: string | null;
  timestamp: string;
  is_confirmed: boolean;
  initialized_by: number | null;
  verify_url: string | null;
  additional_info?: string | null;
  meta: {
    consent_type: ConsentTypeRecordType;
    order_number: string | null;
  };
};

export type PortalUserListRecordType = {
  id: number;
  name: string;
  description?: string;
  consent_type?: ConsentTypeRecordType;
};

export type ContactRecordType = {
  id: number;
  first_name?: string;
  last_name?: string;
  email: string;
};

export type UserListRecordType = {
  id: number;
  name: string;
  description?: string;
  brevo_id?: number;
  is_public: boolean;
  consent_type?: number;
  meta: {
    data_count: number;
    consent_type?: ConsentTypeRecordType;
  };
};

export type UserListDataRecordType = {
  id: number;
  created_at: string;
  user: number;
  user_list: number;
  meta: {
    user: UserRecordType;
    user_list: UserListRecordType;
  };
};
export enum EmailContactInfoTypeEnum {
  COMMUNICATION = "COMMUNICATION",
  INVOICE = "INVOICE",
}

export type EmailJobStatusFieldType =
  | "waiting_for_transfer_count"
  | "transfered_count"
  | "sent_count"
  | "blocked_count"
  | "error_count";

export type EmailJobRelatedResourceType =
  | "user"
  | "staff_member"
  | "booking"
  | "invoice"
  | "interest"
  | "enrollment"
  | "user_list"
  | "eventlecturer"
  | "organization"
  | "order"
  | "order_item"
  | "event_location";

export type EmailRecipientRecordType = {
  related_resource: EmailJobRelatedResourceType;
  related_id: number;
  related_object_name?: string;
  generated_document?: number;
  file?: number;
  contact_info_type?: EmailContactInfoTypeEnum;
  recipient_role?: string | null;
  event?: number;
};

export type UndeliverableEmailRecordType = {
  id: number;
  name: string;
  failure_reason: "no_consent_given" | "no_email_address";
};

/** An address the current recipient set would mail more than once. */
export type DuplicateEmailRecipientRecordType = {
  to: string;
  name: string | null;
  count: number;
};

export type EmailJobRecordType = {
  id: Identifier;
  attachments: number[] | FileRecordType[];
  body?: null | string;
  cc?: string;
  is_test: boolean;
  sender?: number;
  content_blocks: { [key: string]: string | null };
  event_blocks: { [key: string]: number[] | null };
  scheduled_at?: string;
  archived_at?: string | null;
  recipients: EmailRecipientRecordType[];
  meta: {
    sender_name: string;
    sender_email: string;
    total_emails: number;
    waiting_for_transfer_count: number;
    sent_count: number;
    blocked_count: number;
    error_count: number;
    transfered_count: number;
    first_subject: string;
    created_by: MinimalUserRecordType;
  };
};

export type EmailRelatedLinkModelType = "event" | "booking" | "invoice" | "interest" | "organization" | "user";

/** Record the email belongs to. `event` is set for bookings, which have no own route. */
export type EmailRelatedLinkType = {
  model: EmailRelatedLinkModelType;
  id: number;
  label: string;
  event?: number | null;
};

export type EmailRecordType = {
  id: Identifier;
  to: string;
  user: number;
  booking?: number;
  invoice?: number;
  event?: number;
  interest?: number;
  enrollment?: number;
  scheduled_at?: string;
  archived_at?: string | null;
  template?: number;
  subject: string | null;
  email_job: number;
  status: string;
  meta: {
    sender_name: string;
    sender_email: string;
    template: string;
    attachments: FileRecordType[];
    related_links: EmailRelatedLinkType[];
  };
};

export type ExternalEmailContentType = {
  id: number;
  subject: string;
  to: string;
  content: string | null;
};

export enum DashboardItemName {
  ActionBar = "actionBar",
  EventOverview = "eventOverview",
  Todos = "todos",
  Timeline = "timeline",
  BookingOverview = "bookingOverview",
  BookingsPerMetric = "bookingsPerMetric",
  BillableEvents = "billableEvents",
  RecentOrders = "recentOrders",
}

export type DashboardItemsOrder = DashboardItemName[];
export type DashboardItemWidths = { [key in DashboardItemName]: number };

export enum UserSettingKey {
  DashboardItemsOrder = "DASHBOARD_ITEMS_ORDER",
  DashboardItemWidths = "DASHBOARD_ITEM_WIDTHS",
}

export type UserSettingValue = string | number | DashboardItemsOrder | DashboardItemWidths;
export type UserSetting = {
  id: number | string;
  key: UserSettingKey;
  value: UserSettingValue;
};

export type MinimalOrganizationRecordType = {
  id: number;
  name: string;
};

export type NonStaffOrganizationRecordType = {
  id: number;
  name: string;
  invoice_contact_info: ContactInfoType | null;
  invoice_dispatch_contact_info: ContactInfoType | null;
  main_contact_info: ContactInfoType | null;
  custom_fields_data?: CustomFieldDataType;
  custom_fields: CustomFieldRecordType[];
};

export type OrganizationRecordType = {
  id: number;
  name: string;
  debit_number: string | null;
  invoice_contact_info: ContactInfoType | null;
  invoice_dispatch_contact_info: ContactInfoType | null;
  main_contact_info: ContactInfoType | null;
  custom_fields_data?: CustomFieldDataType;
  archived_at: string | null;
  files: FileRecordType[];
};

export type OrganizationUserRecordType = {
  id: number;
  organization: number;
  user: number;
  can_edit_organization: boolean;
  can_enroll: boolean;
  can_manage_users: boolean;
  custom_fields_data: CustomFieldDataType;
  meta: {
    organization: MinimalOrganizationRecordType;
    user: MinimalUserRecordType;
    custom_fields_meta?: CustomFieldMetaType;
  };
};

/**
 * Portal-facing membership (mirrors `OrganizationMemberSerializer`): the person is
 * nested rather than an id, because an org admin creates them along with the
 * membership and never gets to look existing users up.
 */
export type NonStaffOrganizationUserRecordType = {
  id: number;
  organization: number;
  user: {
    id: number;
    first_name: string;
    last_name: string;
    title: string | null;
    main_contact_info: { email: string | null; phone: string | null } | null;
  };
  can_edit_organization: boolean;
  can_enroll: boolean;
  can_manage_users: boolean;
  has_login: boolean;
  custom_fields: CustomFieldRecordType[];
  custom_fields_data: CustomFieldDataType;
  created_at: string;
  updated_at: string;
  meta: {
    custom_fields_meta?: CustomFieldMetaType;
  };
};

export type UserListConfigurationRecordType = {
  id: number;
  name: string;
  resource: string;
  filter_values: Record<string, unknown>;
  hidden_columns: string[];
  column_order: string[];
  sort_field?: string;
  sort_order?: "ASC" | "DESC";
  is_fullscreen: boolean;
};

export type ListConfigurationRecordType = {
  id: number;
  name: string;
  storeKey: string;
  filterValues: Record<string, unknown>;
  hiddenColumns: string[];
  columnOrder: string[];
  sortField?: string;
  sortOrder?: "ASC" | "DESC";
  isFullscreen: boolean;
};
